import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import {
  Flex,
  Heading,
  Field,
  InputGroup,
  Spacer,
  Divider,
  Text,
  Message,
  Button,
  Grid,
  CheckBox,
  FormRow,
} from '@oliasoft-open-source/react-ui-library';
import { unum } from '@oliasoft-open-source/units';
import { getResolver } from 'src/validation/resolver';
import translations from 'src/internationalisation/translation-map.json';
import {
  Input,
  NumberInput,
  Select,
  CheckBoxList,
  UnitInput,
  InputWithVariable,
  SingleCheckBox,
  ControlledText,
  TextArea,
} from 'client/common/form-inputs';
import {
  convertToStorageUnits,
  convertToWithUnits,
  getStorageUnitByQuantityType,
  getUnitCost,
} from 'client/common/units/units';
import { convertToKebabCase } from 'client/common/utils/string-utils';
import { CostType, QuantityType } from 'src/enums/cost-setup';
import { VolumeLinkFieldsType, LengthLinkFieldsType, HoleLengthLinkFieldsType } from 'src/enums/settings';
import { generatePath } from 'store/navigation/navigation';
import { routes } from 'routes/routes';
import { settingsCostItemsSchemaValidator } from 'schemas/ajv-validators';
import { useAutoSave } from 'src/common/auto-save/use-auto-save';
import { autoSaveWait } from 'src/config/config';
import { initialCostItem } from 'src/store/entities/templates/templates';
import {
  selectCostCodesCodeAndNameToIdMap,
  selectCostCodesIdToCodeAndNameMap,
  selectCostCodesItemOptions,
} from 'src/store/entities/cost-codes/selectors';
import { selectPriceBookCompanyItems } from 'src/store/entities/price-book/selectors';
import { mapNameToCode, validateValue } from 'src/views/cost-setup/helpers/check-for-valid-id';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { getSelectedId } from 'store/entities/templates/selectors';

import styles from '../templates.module.less';

const CostItemForm = ({
  entity,
  costItem,
  templates,
  selectedId,
  updateCostItem,
  currenciesOptions,
  groupedOperations,
  costCodesItems,
  costCodesIdToCodeAndNameMap,
  costCodesCodeAndNameToIdMap,
  depthUnit,
  volumeUnit,
  priceBookCompanyItems,
}) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const navigate = useNavigate();

  const isImportedFromPriceBook = !!costItem?.priceBookItemId || !!costItem?.vendor;
  const isLinkedToPriceBook = isImportedFromPriceBook && costItem?.isLinked;
  const isItemDeletedFromPriceBook = costItem?.isLinked && !costItem?.priceBookItemId;
  const linkingMessage = isItemDeletedFromPriceBook
    ? translations.priceBook_thisCostItemIsDeletedFromPriceBook
    : translations.priceBook_thisCostItemIsConnectedToThePriceBook;
  const linkedPriceBook = priceBookCompanyItems.find(
    (item) => costItem?.priceBookItemId && item.priceBookItemId === costItem?.priceBookItemId,
  );
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: initialCostItem,
    resolver: getResolver(settingsCostItemsSchemaValidator),
  });
  useEffect(() => {
    const costData = costItem || initialCostItem;
    const convertedItem = convertToWithUnits(
      costData,
      ['volume', 'length', 'holeLength'],
      { volume: 'm3', length: 'm', holeLength: 'm' },
      { volume: volumeUnit, length: depthUnit, holeLength: depthUnit },
      true,
    );

    convertedItem.unitCost = getStorageUnitByQuantityType(costData)
      ? unum(
          convertedItem.unitCost,
          getStorageUnitByQuantityType(convertedItem),
          getUnitCost(convertedItem, depthUnit, volumeUnit),
        )
      : convertedItem.unitCost;
    convertedItem.excessNumber = getStorageUnitByQuantityType(costData)
      ? unum(
          convertedItem.excessNumber,
          getUnitCost(convertedItem, depthUnit, volumeUnit),
          getStorageUnitByQuantityType(convertedItem),
        )
      : convertedItem.excessNumber;

    reset({
      ...convertedItem,
      costCodesId: validateValue(convertedItem.costCodesId, costCodesItems, costCodesIdToCodeAndNameMap),
      excessNumber: convertedItem.excessNumber || 0,
      holeLength: convertedItem.holeLength || `0|${depthUnit}`,
    });
    return () => {
      debounceAddCostItem.current.flush();
    };
  }, [costItem?.costItemId, reset]);

  const debounceAddCostItem = useRef(debounce(updateCostItem, autoSaveWait));
  const onSubmit = handleSubmit((data) => {
    if (!isDirty) return;
    const convertedData = convertToStorageUnits(data, ['volume', 'length', 'holeLength'], {
      volume: 'm3',
      length: 'm',
      holeLength: 'm',
    });

    convertedData.unitCost = getStorageUnitByQuantityType(data)
      ? unum(convertedData.unitCost, getUnitCost(data, depthUnit, volumeUnit), getStorageUnitByQuantityType(data))
      : data.unitCost;

    convertedData.excessNumber = getStorageUnitByQuantityType(data)
      ? unum(convertedData.excessNumber, getStorageUnitByQuantityType(data), getUnitCost(data, depthUnit, volumeUnit))
      : data.excessNumber;

    convertedData.costCodesId = mapNameToCode(convertedData.costCodesId, costCodesCodeAndNameToIdMap);
    debounceAddCostItem.current(convertedData, templates, selectedId, entity, company);
  });
  useAutoSave(onSubmit, watch);

  const operations = watch('operations');
  const isFirstOperationsRun = useRef(true);
  useEffect(() => {
    if (isFirstOperationsRun.current) {
      isFirstOperationsRun.current = false;
      return;
    }
    onSubmit()?.then(() => debounceAddCostItem.current.flush());
  }, [operations]);

  const typeList = [
    { label: t(translations.costSetup_dayRate), value: CostType.DayRate },
    { label: t(translations.costSetup_lumpSum), value: CostType.LumpSum },
    { label: t(translations.costSetup_quantity), value: CostType.Quantity },
  ];

  const quantityList = [
    {
      label: t(translations.costSetup_itemCount),
      value: QuantityType.ItemCount,
    },
    {
      label: t(translations.costSetup_tubularLength),
      value: QuantityType.Length,
    },
    {
      label: t(translations.costSetup_holeLength),
      value: QuantityType.HoleLength,
    },
    {
      label: t(translations.costSetup_volume),
      value: QuantityType.Volume,
    },
  ];

  const { type, currency, quantity, isStandalone, isDistributed } = watch();
  const selectedUnit = () => {
    if (quantity === QuantityType.ItemCount) return 'item';
    return quantity === QuantityType.Length || quantity === QuantityType.HoleLength ? depthUnit : volumeUnit;
  };
  const extraTimeOptions = [
    { label: t(translations.hours), value: 'hours' },
    { label: t(translations.days), value: 'days' },
  ];
  return (
    <div className={`${styles.scrollWrapper} ${styles.costFormWrapper}`}>
      <div className={styles.scrollArea}>
        <Grid gap>
          <Heading top>{watch('name')}</Heading>
          {isImportedFromPriceBook && (
            <Message
              message={{
                content: (
                  <Flex justifyContent={'space-between'} gap="var(--padding)" alignItems="center">
                    <div style={{ flex: 1 }}>
                      <Text>
                        {t(
                          isLinkedToPriceBook
                            ? linkingMessage
                            : translations.priceBook_thisCostItemIsNotConnectedToThePriceBook,
                        )}
                      </Text>
                    </div>
                    {!isItemDeletedFromPriceBook && isLinkedToPriceBook && (
                      <span style={{ flexShrink: 0 }}>
                        <Button
                          small
                          label={t(translations.costSetup_viewPriceBook)}
                          onClick={() => {
                            const path = generatePath(routes.companySettings.nested.priceBook, { company }, true);
                            const navigateState = linkedPriceBook
                              ? {
                                  priceBookId: linkedPriceBook.priceBookId,
                                  name: linkedPriceBook.priceBookName,
                                }
                              : null;
                            navigate(path, {
                              state: navigateState,
                            });
                          }}
                          testId={`cost-item-form-button-${convertToKebabCase(
                            t(translations.costSetup_viewPriceBook),
                          )}`}
                        />
                      </span>
                    )}
                  </Flex>
                ),
                type: 'Info',
                visible: true,
              }}
            />
          )}
        </Grid>
        <Spacer />
        <form>
          <FormRow>
            <Field label={t(translations.name)}>
              <Input
                name="name"
                control={control}
                errors={errors}
                width="345px"
                disabled={isImportedFromPriceBook}
                testId={`cost-item-form-input-${convertToKebabCase(t(translations.name))}`}
              />
            </Field>
            <Field label={t(translations.code)}>
              <Select
                name="costCodesId"
                control={control}
                errors={errors}
                options={costCodesItems}
                width="200px"
                clearable
                disabled={isImportedFromPriceBook}
                testId={`cost-item-form-select-${convertToKebabCase(t(translations.code))}`}
              />
            </Field>
            <Field label={t(translations.type)}>
              <Select
                name="type"
                control={control}
                errors={errors}
                options={typeList}
                onChange={() => setValue('isStandalone', false)}
                width="125px"
                disabled={isImportedFromPriceBook}
                testId={`cost-item-form-select-${convertToKebabCase(t(translations.type))}`}
              />
            </Field>
          </FormRow>
          <Spacer height="var(--padding-xs)" />
          {type !== CostType.Quantity && (
            <FormRow>
              {type === CostType.DayRate && (
                <Field
                  label={t(translations.costSetup_addExtraTime)}
                  helpText={t(translations.costSetup_extraTimeTooltip)}
                >
                  <span style={{ paddingBottom: '3px' }}>
                    <InputGroup width="160px">
                      <NumberInput
                        name="extraTimeValue"
                        control={control}
                        errors={errors}
                        width="80px"
                        testId={`cost-item-form-input-${convertToKebabCase(
                          t(translations.costSetup_addExtraTime),
                        )}-value`}
                      />
                      <Select
                        name="extraTimeUnit"
                        control={control}
                        errors={errors}
                        options={extraTimeOptions}
                        width="80px"
                        testId={`cost-item-form-select-${convertToKebabCase(
                          t(translations.costSetup_addExtraTime),
                        )}-unit`}
                      />
                    </InputGroup>
                  </span>
                </Field>
              )}
              <Field label={t(translations.cost)}>
                <InputGroup width="200px">
                  <NumberInput
                    name="cost"
                    control={control}
                    errors={errors}
                    width="100px"
                    disabled={isImportedFromPriceBook}
                    testId={`cost-item-form-input-${convertToKebabCase(t(translations.cost))}-value`}
                  />
                  <Select
                    name="currency"
                    control={control}
                    errors={errors}
                    options={currenciesOptions}
                    disabled={isImportedFromPriceBook}
                    testId={`cost-item-form-select-${convertToKebabCase(t(translations.cost))}-currency`}
                  />
                </InputGroup>
              </Field>
            </FormRow>
          )}
          {type === CostType.Quantity && (
            <FormRow>
              <Field label={t(translations.costSetup_quantity)}>
                <Select
                  name="quantity"
                  control={control}
                  errors={errors}
                  options={quantityList}
                  width="150px"
                  disabled={isImportedFromPriceBook}
                  testId={`cost-item-form-select-${convertToKebabCase(t(translations.costSetup_quantity))}`}
                />
              </Field>
              {quantity === QuantityType.Length && (
                <Field label={t(translations.costSetup_length)}>
                  <InputWithVariable
                    name="length"
                    unitkey={QuantitiesTypes.Depth}
                    unit={depthUnit}
                    errors={errors}
                    control={control}
                    linkFields={LengthLinkFieldsType}
                    width="150px"
                    noConversion
                  />
                </Field>
              )}
              {quantity === QuantityType.HoleLength && (
                <Field label={t(translations.length)}>
                  <InputWithVariable
                    name="holeLength"
                    unitkey={QuantitiesTypes.Depth}
                    unit={depthUnit}
                    errors={errors}
                    control={control}
                    linkFields={HoleLengthLinkFieldsType}
                    width="150px"
                    noConversion
                  />
                </Field>
              )}
              {quantity === QuantityType.Volume && (
                <Field label={t(translations.costSetup_volume)}>
                  <InputWithVariable
                    name="volume"
                    unitkey={QuantitiesTypes.Volume}
                    unit={volumeUnit}
                    errors={errors}
                    control={control}
                    linkFields={VolumeLinkFieldsType}
                    width="150px"
                    noConversion
                  />
                </Field>
              )}
              {quantity === QuantityType.ItemCount && (
                <Field label={t(translations.costSetup_itemCount)}>
                  <NumberInput
                    name="itemCount"
                    control={control}
                    errors={errors}
                    width="150px"
                    testId={`cost-item-form-input-${convertToKebabCase(t(translations.costSetup_itemCount))}`}
                  />
                </Field>
              )}
              <Field label={t(translations.costSetup_addExcess)}>
                <UnitInput
                  name="excessNumber"
                  unit={selectedUnit()}
                  control={control}
                  errors={errors}
                  width="105px"
                  enableDisplayRounding
                  testId={`cost-item-form-input-${convertToKebabCase(t(translations.costSetup_addExcess))}`}
                />
              </Field>
              <Field label={t(translations.costSetup_unitCost)}>
                <UnitInput
                  name="unitCost"
                  unit={`${currency}/${selectedUnit()}`}
                  control={control}
                  errors={errors}
                  width="125px"
                  disabled={isImportedFromPriceBook}
                  enableDisplayRounding
                  testId={`cost-item-form-input-${convertToKebabCase(t(translations.costSetup_unitCost))}`}
                />
              </Field>
            </FormRow>
          )}
          <Spacer height="var(--padding-xs)" />
          <Field label={t(translations.notes_notes)}>
            <TextArea
              name="notes"
              control={control}
              errors={errors}
              rows={5}
              testId={`cost-item-form-textarea-${convertToKebabCase(t(translations.notes_notes))}`}
            />
          </Field>
          <Divider margin={'var(--padding)'} />
          {type !== CostType.DayRate && (
            <SingleCheckBox
              control={control}
              errors={errors}
              label={t(translations.costSetup_distributeCost)}
              name="isDistributed"
              checked={isDistributed}
              onChange={(e) => {
                const { checked } = e.target;
                setValue('isDistributed', checked);
                if (checked) {
                  setValue('isStandalone', false);
                }
              }}
            />
          )}
          {type === CostType.LumpSum && (
            <SingleCheckBox
              control={control}
              errors={errors}
              onChange={(e) => {
                const { checked } = e.target;
                setValue('isStandalone', checked);
                if (checked) {
                  setValue('operations', []);
                  setValue('isDistributed', false);
                }
              }}
              label={t(translations.costSetup_planningCost)}
              name="isStandalone"
              checked={isStandalone}
            />
          )}
          <Spacer height="var(--padding-xs)" />
          <Heading>{t(translations.risks_applyToOperations)}</Heading>
          <Spacer height="var(--padding-xs)" />
          <ControlledText
            name="operations"
            control={control}
            disabled={isStandalone}
            onClick={(field) => {
              const allOperationIds = groupedOperations?.flatMap((item) =>
                item.operations.map((operation) => operation.id),
              );
              field.onChange(allOperationIds);
            }}
            text={t(translations.checkAll)}
          />
          <Spacer width="var(--padding-sm)" />
          <ControlledText
            name="operations"
            control={control}
            disabled={isStandalone}
            onClick={(field) => {
              field.onChange([]);
            }}
            text={t(translations.uncheckAll)}
          />
          <Spacer height="var(--padding-xs)" />
          <Grid columns="1fr 1fr">
            {groupedOperations?.map((section) => {
              return (
                <div key={section.linkedSectionId}>
                  <Controller
                    name="operations"
                    control={control}
                    render={({ field }) => (
                      <CheckBox
                        testId={`section-${section.sectionId}-checkbox`}
                        onChange={(e) => {
                          const sectionIds = section.operations.map((item) => item.id);
                          const currentItems = watch('operations') || [];

                          const nextItems = e.target.checked
                            ? [...new Set([...currentItems, ...sectionIds])]
                            : currentItems.filter((id) => !sectionIds.includes(id));
                          field.onChange(nextItems);
                        }}
                        label={<Text bold>{`${section.sectionName} ${section.sectionTypeName ?? ''}`}</Text>}
                        value={section.sectionId}
                        checked={
                          watch('operations') &&
                          section.operations.every((item) => watch('operations').includes(item.id))
                        }
                        disabled={isStandalone}
                      />
                    )}
                  />
                  <div style={{ paddingLeft: '24px' }}>
                    <CheckBoxList
                      items={section.operations}
                      control={control}
                      errors={errors}
                      name="operations"
                      disabled={isStandalone}
                    />
                  </div>
                  <Spacer height="var(--padding-xs)" />
                </div>
              );
            })}
          </Grid>
        </form>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    userSettings: { currencies },
  } = state.entities;
  const currenciesOptions = currencies.map((currency) => ({
    label: currency.code,
    value: currency.code,
  }));
  return {
    currenciesOptions,
    costCodesItems: selectCostCodesItemOptions(state),
    costCodesIdToCodeAndNameMap: selectCostCodesIdToCodeAndNameMap(state),
    costCodesCodeAndNameToIdMap: selectCostCodesCodeAndNameToIdMap(state),
    selectedId: getSelectedId(state),
    priceBookCompanyItems: selectPriceBookCompanyItems(state.entities),
  };
};

const Container = connect(mapStateToProps)(CostItemForm);

export { Container as CostItemForm };
